import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import axios from "axios";
import { Table, Row, Col, Form, Pagination, Spinner, Alert } from "react-bootstrap";
import PageLayout from "../../../../layouts/PageLayout";
import { Link } from "react-router-dom";

const formatAmount = (amount) => {
    if (amount === null || amount === undefined || String(amount).trim() === "") {
        return "-";
    }
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) {
        return "-";
    }

    return numericAmount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const FinanceAmount = ({ amount, className = "" }) => (
    <span className={className}>
        {formatAmount(amount) === "-" ? "-" : `PKR ${formatAmount(amount)}`}
    </span>
);

export default function ProductFinanceReport() {
    const { t } = useContext(TranslatorContext);
    const API_BASE = `${process.env.REACT_APP_API_URL}/admin/finance/product`;

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        lastPage: 1,
        perPage: 20,
        total: 0,
    });

    const fetchFinanceData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(API_BASE, {
                params: {
                    page: pagination.currentPage,
                    per_page: pagination.perPage,
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const paginator = response.data?.data;
            const rows = Array.isArray(paginator) ? paginator : paginator?.data;
            setProducts(Array.isArray(rows) ? rows : []);
            setPagination((current) => ({
                ...current,
                currentPage: Number(paginator?.current_page) || 1,
                lastPage: Number(paginator?.last_page) || 1,
                perPage: Number(paginator?.per_page) || current.perPage,
                total: Number(paginator?.total) || 0,
            }));
        } catch (fetchError) {
            console.error("Error fetching product finance data:", fetchError);
            setProducts([]);
            setError(fetchError.response?.data?.message || t("Failed to load product finance report."));
        } finally {
            setLoading(false);
        }
    }, [API_BASE, pagination.currentPage, pagination.perPage, t]);

    useEffect(() => {
        fetchFinanceData();
    }, [fetchFinanceData]);

    const handlePerPageChange = (event) => {
        setPagination((current) => ({
            ...current,
            perPage: Number(event.target.value),
            currentPage: 1,
        }));
    };

    const pageExpensesTotal = products.reduce((total, product) => {
        const amount = Number(product.total_expenses_pkr);
        return Number.isFinite(amount) ? total + amount : total;
    }, 0);
    const pageProfitTotal = products.reduce((total, product) => {
        const amount = Number(product.profit_pkr);
        return Number.isFinite(amount) ? total + amount : total;
    }, 0);

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t("Product Finance Report")}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t("finance report")}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col md={6} className="mb-4">
                    <div
                        className="mc-card p-4 h-100"
                        style={{ borderLeft: "5px solid #dc3545", background: "#f8f9fa" }}
                    >
                        <span className="text-muted fw-bold text-uppercase d-block mb-1" style={{ fontSize: 12 }}>
                            {t("Expenses Total (Current Page)")}
                        </span>
                        <h3 className="mb-0 text-danger fw-bold">
                            PKR {formatAmount(pageExpensesTotal)}
                        </h3>
                    </div>
                </Col>
                <Col md={6} className="mb-4">
                    <div
                        className="mc-card p-4 h-100"
                        style={{ borderLeft: "5px solid #4f46e5", background: "#f8f9fa" }}
                    >
                        <span className="text-muted fw-bold text-uppercase d-block mb-1" style={{ fontSize: 12 }}>
                            {t("Profit Total (Current Page)")}
                        </span>
                        <h3 className={`mb-0 fw-bold ${pageProfitTotal >= 0 ? "text-primary" : "text-danger"}`}>
                            PKR {formatAmount(pageProfitTotal)}
                        </h3>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header p-3 d-flex justify-content-between align-items-center">
                            <h5 className="mc-card-title mb-0">{t("Product Finance Breakdown")}</h5>
                            <Form.Select
                                aria-label={t("Rows per page")}
                                value={pagination.perPage}
                                onChange={handlePerPageChange}
                                style={{ width: "auto" }}
                            >
                                {[10, 20, 50, 100].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>{pageSize} {t("per page")}</option>
                                ))}
                            </Form.Select>
                        </div>

                        {error && (
                            <Alert variant="danger" className="m-3 mb-0">
                                {error}
                            </Alert>
                        )}

                        <div
                            className="w-100"
                            style={{
                                maxWidth: "100%",
                                overflowX: "auto",
                                WebkitOverflowScrolling: "touch",
                            }}
                        >
                            <Table
                                bordered
                                hover
                                className="align-middle mb-0"
                                style={{ minWidth: "1650px", width: "100%" }}
                            >
                                <thead className="table-primary">
                                    <tr>
                                        <th className="text-center text-nowrap">#</th>
                                        <th className="text-nowrap">{t("Product")}</th>
                                        <th className="text-nowrap">{t("Order")}</th>
                                        <th className="text-nowrap">{t("Sale Price")}</th>
                                        <th className="text-nowrap">{t("Shipping")}</th>
                                        <th className="text-nowrap">{t("Buying Price")}</th>
                                        <th className="text-nowrap">{t("Exchange Rate")}</th>
                                        <th className="text-nowrap" style={{ minWidth: 220 }}>{t("Staff Payments")}</th>
                                        <th className="text-nowrap" style={{ minWidth: 190 }}>{t("Market Placer")}</th>
                                        <th className="text-nowrap" style={{ minWidth: 155 }}>{t("Total Expenses")}</th>
                                        <th className="text-nowrap" style={{ minWidth: 120 }}>{t("Profit")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={11} className="text-center py-5">
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                {t("Loading finance data...")}
                                            </td>
                                        </tr>
                                    ) : products.length === 0 ? (
                                        <tr>
                                            <td colSpan={11} className="text-center py-4">
                                                {t("No financial records found.")}
                                            </td>
                                        </tr>
                                    ) : (
                                        products.map((product, index) => (
                                            <tr key={product.id}>
                                                <td className="text-center">
                                                    {(pagination.currentPage - 1) * pagination.perPage + index + 1}
                                                </td>
                                                <td style={{ minWidth: 200, maxWidth: 220 }}>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {product.card_img ? (
                                                            <img
                                                                src={`${process.env.REACT_APP_IMG_URL}${product.card_img}`}
                                                                alt={product.title || ""}
                                                                className="rounded"
                                                                style={{ width: 48, height: 48, objectFit: "cover" }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="rounded bg-light d-flex align-items-center justify-content-center text-muted"
                                                                style={{ width: 48, height: 48, fontSize: 9 }}
                                                            >
                                                                {t("No image")}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <small className="text-muted">{product.product_code || "—"}</small>
                                                            <div className="fw-semibold">{product.title || "—"}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div>{product.order?.order_number || "—"}</div>
                                                    <small className="text-muted">{product.order?.delivery_status || "—"}</small>
                                                </td>
                                                <td><FinanceAmount amount={product.sell_price_pkr} /></td>
                                                <td><FinanceAmount amount={product.shipping_pkr} /></td>
                                                <td><FinanceAmount amount={product.buying_price_pkr} /></td>
                                                <td>{formatAmount(product.exchange_rate)}</td>
                                                <td style={{ minWidth: 190 }}>
                                                    {[
                                                        { role: t("Lister"), person: product.lister },
                                                        { role: t("Checker"), person: product.checker },
                                                        { role: t("Issuer"), person: product.issuer },
                                                    ]
                                                        .filter(({ person }) => Number(person?.amount_pkr) > 0)
                                                        .map(({ role, person }) => (
                                                            <div key={role} className="mb-1">
                                                                <div className="fw-semibold">{person.name || role}</div>
                                                                <small className="text-muted">
                                                                    {role}: <FinanceAmount amount={person.amount_pkr} />
                                                                </small>
                                                            </div>
                                                        ))}
                                                </td>
                                                <td>
                                                    <div>{product.mplacer?.name || "—"}</div>
                                                    <small className="text-muted">
                                                        {t("Commission")}: <FinanceAmount amount={product.mplacer?.commission_pkr} />
                                                    </small>
                                                </td>
                                                <td className="fw-semibold text-danger">
                                                    <FinanceAmount amount={product.total_expenses_pkr} />
                                                </td>
                                                <td className={`fw-bold ${Number(product.profit_pkr) >= 0 ? "text-success" : "text-danger"}`}>
                                                    <FinanceAmount amount={product.profit_pkr} />
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-3">
                            <small className="text-muted">
                                {pagination.total === 0
                                    ? t("No records")
                                    : `${(pagination.currentPage - 1) * pagination.perPage + 1}–${Math.min(
                                        pagination.currentPage * pagination.perPage,
                                        pagination.total
                                    )} ${t("of")} ${pagination.total}`}
                            </small>
                            <Pagination className="mb-0">
                                <Pagination.Prev
                                    disabled={loading || pagination.currentPage <= 1}
                                    onClick={() => setPagination((current) => ({
                                        ...current,
                                        currentPage: current.currentPage - 1,
                                    }))}
                                />
                                <Pagination.Item active>{pagination.currentPage}</Pagination.Item>
                                <Pagination.Next
                                    disabled={loading || pagination.currentPage >= pagination.lastPage}
                                    onClick={() => setPagination((current) => ({
                                        ...current,
                                        currentPage: current.currentPage + 1,
                                    }))}
                                />
                            </Pagination>
                        </div>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}
